import React, { useState, useRef } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { AppCard } from "../../components/ui";
import { Toast } from "primereact/toast";
import { LogsScreenProps } from "./types";
import { utils } from "../../utils/utils";
import JsonPreviewModal from "./JsonPreviewModal";

const LogsScreen: React.FC<LogsScreenProps> = ({
  logs,
  searchTerm,
  onSearchChange,
  onExportLogs,
  onStartNew,
  onCancel,
  isExporting = false,
  exportError = null,
  onClearExportError
}) => {
  const [selectedJsonData, setSelectedJsonData] = useState<any>(null);
  const [isJsonModalOpen, setIsJsonModalOpen] = useState(false);
  const toastRef = useRef<Toast>(null);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSearchChange(event.target.value);
  };

  const handleDetailsClick = (details: string) => {
    const parsedData = utils.parseJsonString(details);
    setSelectedJsonData(parsedData);
    setIsJsonModalOpen(true);
  };

  const renderDetailsCell = (log: any) => {
    if (!log.details && !log.error) {
      return '-';
    }

    const detailsText = log.details || log.error || '';
    const isJson = utils.isJsonString(detailsText);

    // Show simplified text in table, full JSON in modal
    let displayText = detailsText;
    if (isJson) {
      try {
        const parsed = JSON.parse(detailsText);
        const fileId = parsed.file_id || log.fileId || log.id || 'N/A';
        displayText = `{"file_id": ${fileId}, "data": {...}}`;
      } catch (e) {
        // If parsing fails, show truncated version
        displayText = detailsText.length > 50
          ? detailsText.substring(0, 50) + '...'
          : detailsText;
      }
    }

    return (
      <span
        className={isJson ? 'text-primary-600 dark:text-primary-400 cursor-pointer underline' : ''}
        onClick={() => isJson && handleDetailsClick(detailsText)}
        style={{ cursor: isJson ? 'pointer' : 'default' }}
        title={isJson ? 'Click to view JSON' : ''}
      >
        {displayText}
      </span>
    );
  };

  // Filter logs based on search term
  const filteredLogs = logs.filter(log => {
    if (!searchTerm.trim()) return true;

    const lowerSearch = searchTerm.toLowerCase();
    return (
      (log.timestamp || log.uploadTimestamp || '').toLowerCase().includes(lowerSearch) ||
      (log.fileId || log.id || '').toString().toLowerCase().includes(lowerSearch) ||
      (log.action || log.purpose || '').toLowerCase().includes(lowerSearch) ||
      (log.details || log.error || '').toLowerCase().includes(lowerSearch) ||
      (log.status || '').toLowerCase().includes(lowerSearch) ||
      (log.referenceNo || log.flexcubeReference || '').toLowerCase().includes(lowerSearch)
    );
  });

  return (
    <>
      <Toast ref={toastRef} />
      <AppCard>
        <AppCard.Header title="Final Logs" />
        <AppCard.Body>
        <div className="mb-4">
          <InputText
            type="text"
            placeholder="Search by date, status, reference, or file ID"
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full"
          />
        </div>

        {exportError && (
          <div className="rounded-lg border border-red-500 bg-gradient-to-br from-red-100 to-red-200 text-red-900 mb-4 py-3 px-4 shadow-sm dark:from-danger-900/20 dark:to-danger-900/30 dark:text-danger-400 dark:border-danger-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <i className="fas fa-exclamation-triangle mr-2"></i>
                <span>{exportError}</span>
              </div>
              <button
                onClick={onClearExportError}
                className="p-0 text-red-700 dark:text-danger-400 hover:text-red-900 bg-transparent border-none cursor-pointer"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>
        )}

        <div className="max-h-[500px] overflow-y-auto border border-gray-300 rounded-md">
          <table className="w-full text-sm">
            <thead className="bg-neutral-800 text-white sticky top-0 z-10">
              <tr>
                <th style={{ width: '150px' }} className="py-3 px-4 text-left">Timestamp</th>
                <th style={{ width: '80px' }} className="py-3 px-4 text-left">File ID</th>
                <th style={{ width: '150px' }} className="py-3 px-4 text-left">Action</th>
                <th className="py-3 px-4 text-left">Details</th>
                <th style={{ width: '100px' }} className="py-3 px-4 text-center">Status</th>
                <th style={{ width: '150px' }} className="py-3 px-4 text-left">Reference No</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map((log, index) => {
                // Use utility function to format action text
                const actionText = utils.formatActionText(log.recordId, index);

                return (
                  <tr key={index} className="border-b border-neutral-200 dark:border-neutral-700 even:bg-neutral-50 dark:even:bg-neutral-800/50">
                    <td className="py-2 px-4">{log.timestamp || log.uploadTimestamp || '-'}</td>
                    <td className="py-2 px-4">{log.fileId || log.id || '-'}</td>
                    <td className="py-2 px-4">{actionText}</td>
                    <td className="py-2 px-4 max-w-[200px] break-all">
                      {renderDetailsCell(log)}
                    </td>
                    <td className="py-2 px-4 text-center">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-bold ${
                          log.status === 'Success' ||
                          log.status === 'Processed' ||
                          (log.status === 'PROCESSED' && !log.details?.toLowerCase().includes('failed'))
                            ? 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-400'
                            : 'bg-danger-100 text-danger-800 dark:bg-danger-900/30 dark:text-danger-400'
                        }`}
                      >
                        {log.status}
                      </span>
                    </td>
                    <td className="py-2 px-4">{log.referenceNo || log.flexcubeReference || ''}</td>
                  </tr>
                );
              })}

              {filteredLogs.length === 0 && logs.length > 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-neutral-500 py-4">
                    No logs found matching &quot;{searchTerm}&quot;
                  </td>
                </tr>
              )}

              {logs.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-neutral-500 py-4">
                    No logs available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3">
          <div>
            <Button
              onClick={onExportLogs}
              disabled={isExporting}
              className="w-full rounded px-6 py-3 text-sm font-medium transition-all border border-transparent h-11 inline-flex items-center justify-center"
              label={isExporting ? 'Exporting...' : 'Export Logs'}
              icon={isExporting ? 'fas fa-spinner fa-spin' : 'fas fa-download'}
            />
          </div>
          <div>
            <Button
              onClick={onStartNew}
              className="w-full rounded px-6 py-3 text-sm font-medium transition-all border border-transparent h-11 inline-flex items-center justify-center"
              label="Start New Payment"
              icon="fas fa-plus"
            />
          </div>
          <div>
            <Button
              onClick={onCancel}
              severity="danger"
              className="w-full rounded px-6 py-3 text-sm font-medium transition-all h-11 inline-flex items-center justify-center"
              label="Cancel"
              icon="fas fa-times"
            />
          </div>
        </div>
      </AppCard.Body>
    </AppCard>

    <JsonPreviewModal
      isOpen={isJsonModalOpen}
      onClose={() => setIsJsonModalOpen(false)}
      title="Transaction Details"
      jsonData={selectedJsonData}
      toastRef={toastRef}
    />
    </>
  );
};

export default LogsScreen;
